import React, { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, GeoJSON } from "react-leaflet";
import "leaflet/dist/leaflet.css";

const normalizeDeptCode = (value) => {
  const str = String(value ?? "").trim();
  if (!str) return "";
  if (/^\d+$/.test(str)) {
    return String(parseInt(str, 10));
  }
  return str.toUpperCase();
};

export default function CarteFrance({
  partenaires = [],
  selectedDepartement = "",
  onSelectDepartement,
}) {
  const [geoJsonData, setGeoJsonData] = useState(null);

  useEffect(() => {
    const loadGeoJSON = async () => {
      try {
        const response = await fetch("/departements.geojson");
        const data = await response.json();
        setGeoJsonData(data);
      } catch (error) {
        console.error("Erreur chargement geojson départements :", error);
        setGeoJsonData(null);
      }
    };

    loadGeoJSON();
  }, []);

  const countsByDept = useMemo(() => {
    const map = {};
    partenaires.forEach((partenaire) => {
      const code = normalizeDeptCode(partenaire?.departement);
      if (!code) return;
      map[code] = (map[code] || 0) + 1;
    });
    return map;
  }, [partenaires]);

  const enhancedGeoJson = useMemo(() => {
    if (!geoJsonData?.features) return null;

    return {
      ...geoJsonData,
      features: geoJsonData.features.map((feature) => {
        const code = normalizeDeptCode(feature?.properties?.code);
        const count = countsByDept[code] || 0;
        return {
          ...feature,
          properties: {
            ...feature.properties,
            code,
            annonceCount: count,
            isSelected:
              normalizeDeptCode(selectedDepartement) === code && !!selectedDepartement,
          },
        };
      }),
    };
  }, [geoJsonData, countsByDept, selectedDepartement]);

  const styleDepartement = (feature) => {
    const count = feature?.properties?.annonceCount || 0;
    const isSelected = feature?.properties?.isSelected;

    if (isSelected) {
      return {
        fillColor: "#4f46e5",
        weight: 2.5,
        color: "#1e1b4b",
        fillOpacity: 0.65,
      };
    }

    if (count >= 1 && count < 3) {
      return {
        fillColor: "#2563eb",
        weight: 1.2,
        color: "#0f172a",
        fillOpacity: 0.55,
      };
    }

    if (count >= 1) {
      return {
        fillColor: "#93c5fd",
        weight: 1,
        color: "#0f172a",
        fillOpacity: 0.45,
      };
    }

    return {
      fillColor: "#e5e7eb",
      weight: 1,
      color: "#334155",
      fillOpacity: 0.3,
    };
  };

  const onEachFeature = (feature, layer) => {
    const code = feature?.properties?.code;
    const nom = feature?.properties?.nom;
    const count = feature?.properties?.annonceCount || 0;

    layer.bindTooltip(
      `${code} — ${nom} • ${count} annonce${count > 1 ? "s" : ""}`,
      {
        sticky: true,
        direction: "top",
      }
    );

    layer.on("click", () => {
      onSelectDepartement?.(code);
    });
  };

  if (!enhancedGeoJson) {
    return (
      <div className="flex h-[520px] items-center justify-center rounded-[24px] border border-slate-200 bg-slate-50 text-slate-500 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400">
        Chargement de la carte…
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-[24px] border border-slate-200 dark:border-slate-800">
      <MapContainer
        center={[46.5, 2.3]}
        zoom={5.7}
        scrollWheelZoom={true}
        style={{ height: "520px", width: "100%" }}
        className="z-0"
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution="&copy; OpenStreetMap contributors"
        />

        <GeoJSON
          data={enhancedGeoJson}
          style={styleDepartement}
          onEachFeature={onEachFeature}
        />
      </MapContainer>

      <div className="flex flex-wrap items-center gap-3 border-t border-slate-200 bg-white px-4 py-3 text-xs text-slate-600 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300">
        <span className="inline-flex items-center gap-2">
          <span className="h-3 w-3 rounded-full bg-[#e5e7eb]" />
          Aucun partenaire
        </span>
        <span className="inline-flex items-center gap-2">
          <span className="h-3 w-3 rounded-full bg-[#93c5fd]" />
          1 à 2 annonces
        </span>
        <span className="inline-flex items-center gap-2">
          <span className="h-3 w-3 rounded-full bg-[#2563eb]" />
          3 annonces ou plus
        </span>
        <span className="inline-flex items-center gap-2">
          <span className="h-3 w-3 rounded-full bg-[#4f46e5]" />
          Département sélectionné
        </span>
      </div>
    </div>
  );
}